import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { X, PlayIcon, PauseIcon } from 'lucide-react';
import { PatternFormat } from 'react-number-format';

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form';
import { UniversalCombobox } from '@/components/common/UniversalCombobox';
import { StartableTaskSelect } from './StartableTaskSelect';
import { getApiErrorMessage } from '@/lib/utils';
import { formatTime, parseTimeToSeconds } from '@/lib/utils/date';
import { useAuth } from '@/features/auth';
import { activitiesApi, activityKeys } from '../api/activities.api';
import { newTaskKeys } from '@/features/new-tasks/api';
import type { Activity } from '../types';
import { ShowTime } from './ActivityShowTime';

// Schemas
const createSchema = z.object({
  taskId: z.number({ invalid_type_error: 'Task is required' }).int().positive('Task is required'),
  description: z.string().trim().min(1, 'Description is required'),
});

const editSchema = z.object({
  userEnteredTimeInSeconds: z.number().int().nonnegative().optional(),
  description: z.string().trim().optional(),
  typeId: z.number().int().positive().optional(),
});

type CreateFormValues = z.infer<typeof createSchema>;
type EditFormValues = z.infer<typeof editSchema>;

export interface ActivityDialogProps {
  /** Activity ID for stop/edit mode. If provided, opens in edit mode. */
  activityId?: number;
  /** Pre-selected task ID for create mode (used from task page). */
  taskId?: number;
  /** Whether to show task selector in create mode. Default: true */
  showTaskSelector?: boolean;
  /** Custom trigger element. If not provided, uses default play/pause button. */
  trigger?: React.ReactNode;
  /** Callback when activity is started or stopped successfully. */
  onSuccess?: () => void;
  /** Callback when dialog closes. */
  onClose?: () => void;
}

export function ActivityDialog({
  activityId,
  taskId,
  showTaskSelector = true,
  trigger,
  onSuccess,
  onClose,
}: ActivityDialogProps) {
  const [open, setOpen] = React.useState(false);
  const [activityData, setActivityData] = React.useState<Activity | null>(null);
  const [showTimer, setShowTimer] = React.useState(true);
  const userEditedTime = React.useRef(false);
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isEdit = !!activityId;

  // Check if something active already — read from cache only, do not trigger a refetch.
  // ActivityTimer (navbar) owns the polling; each row-level dialog must not fire its own requests.
  const { data: activeActivity } = useQuery({
    queryKey: activityKeys.active(),
    queryFn: async () => {
      try {
        return await activitiesApi.getActive();
      } catch {
        return null;
      }
    },
    retry: 1,
    refetchOnMount: false,
  });

  const form = useForm<CreateFormValues | EditFormValues>({
    resolver: zodResolver(isEdit ? editSchema : createSchema),
    defaultValues: isEdit
      ? { userEnteredTimeInSeconds: undefined, description: '', typeId: undefined }
      : { taskId: taskId, description: '' },
  });

  // Load activity data for edit mode
  React.useEffect(() => {
    if (open && activityId) {
      activitiesApi.getById(activityId)
        .then((data) => {
          setActivityData(data);
          form.reset({
            userEnteredTimeInSeconds: data.userEnteredTimeInSeconds ?? 0,
            description: data.description || '',
            typeId: data.typeId ?? undefined,
          });
        })
        .catch(() => {
          toast.error(t('errorLoadingActivity'));
        });
    }
  }, [open, activityId, form, t]);

  // Reset form when dialog closes
  React.useEffect(() => {
    if (!open) {
      form.reset(isEdit
        ? { userEnteredTimeInSeconds: undefined, description: '', typeId: undefined }
        : { taskId: taskId, description: '' }
      );
      setActivityData(null);
      setShowTimer(true);
      userEditedTime.current = false;
    }
  }, [open, isEdit, taskId, form]);

  const { mutate: createMutation, isPending: isCreating } = useMutation({
    mutationFn: (data: CreateFormValues) => activitiesApi.start(data),
    onSuccess: () => {
      toast.success(t('activityStarted'));
      queryClient.invalidateQueries({ queryKey: ['getAllActivities'] });
      queryClient.invalidateQueries({ queryKey: activityKeys.active() });
      queryClient.invalidateQueries({ queryKey: ['getAllTasks'] });
      // Also refresh new-tasks list + inlined viewer adjustment activities so the
      // expanded row picks up the new activity without a manual page refresh.
      queryClient.invalidateQueries({ queryKey: newTaskKeys.all });
      form.reset();
      setOpen(false);
      onSuccess?.();
      onClose?.();
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('failedToStartActivity')));
    },
  });

  const { mutate: updateMutation, isPending: isUpdating } = useMutation({
    mutationFn: (data: EditFormValues) => activitiesApi.stop(activityId!, data),
    onSuccess: () => {
      toast.success(t('activityStopped'));
      queryClient.invalidateQueries({ queryKey: ['getAllActivities'] });
      queryClient.invalidateQueries({ queryKey: activityKeys.active() });
      queryClient.invalidateQueries({ queryKey: ['getAllTasks'] });
      // Also refresh new-tasks list + inlined viewer adjustment activities so the
      // expanded row picks up the new activity without a manual page refresh.
      queryClient.invalidateQueries({ queryKey: newTaskKeys.all });
      form.reset();
      setOpen(false);
      onSuccess?.();
      onClose?.();
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('failedToStopActivity')));
    },
  });

  const handleOpenChange = (isOpen: boolean) => {
    if (activeActivity && !isEdit) {
      toast.error(t('status.alreadyRunning'));
    } else {
      setOpen(isOpen);
      if (!isOpen) {
        queryClient.invalidateQueries({ queryKey: ['getAllActivities'] });
        onClose?.();
      }
    }
  };

  const onSubmit = (values: CreateFormValues | EditFormValues) => {
    if (isEdit) {
      const editValues = { ...values } as EditFormValues;

      // Only send userEnteredTimeInSeconds if the user manually edited the time field
      if (!userEditedTime.current) {
        delete editValues.userEnteredTimeInSeconds;
      }

      updateMutation(editValues);
    } else {
      createMutation(values as CreateFormValues);
    }
  };

  const isPending = isCreating || isUpdating;

  const defaultTrigger = (
    <button className="p-1 hover:bg-muted rounded">
      {isEdit ? (
        <PauseIcon className="w-4 h-4" />
      ) : (
        <PlayIcon className="w-4 h-4" />
      )}
    </button>
  );

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogTrigger asChild>
        {trigger || defaultTrigger}
      </AlertDialogTrigger>

      <AlertDialogContent className="p-6 max-w-lg">
        <div className="flex items-center justify-between border-b pb-4 border-[#D9D9D9]">
          <AlertDialogTitle className="text-xl font-semibold">
            {isEdit ? t('update') : t('new')} {t('activity')}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <button onClick={() => setOpen(false)} aria-label="Close dialog">
              <X size={24} />
            </button>
          </AlertDialogDescription>
        </div>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="mt-4 flex flex-col gap-4"
            noValidate
          >
            {/* Create Mode */}
            {!isEdit && (
              <>
                {showTaskSelector && (
                  <FormField
                    control={form.control}
                    name="taskId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('task')} *</FormLabel>
                        <FormControl>
                          <StartableTaskSelect
                            value={field.value as number | undefined}
                            onChange={(id) => field.onChange(id ?? undefined)}
                            placeholder={t('selectTask')}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                )}

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('description')} *</FormLabel>
                      <FormControl>
                        <Textarea placeholder={t('enterDescription')} {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </>
            )}

            {/* Edit Mode */}
            {isEdit && (
              <>
                <FormField
                  control={form.control}
                  name="userEnteredTimeInSeconds"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('userEnteredTime')}</FormLabel>
                      <FormControl>
                        <div className="relative">
                          {showTimer && activityData && (
                            <div className="absolute left-3 bg-white top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none z-10">
                              <ShowTime data={activityData} />
                            </div>
                          )}
                          <PatternFormat
                            format="##:##:##"
                            value={formatTime((field.value as number) ?? 0)}
                            onChange={(e) => {
                              userEditedTime.current = true;
                              field.onChange(parseTimeToSeconds(e.target.value));
                            }}
                            customInput={Input}
                            onFocus={() => setShowTimer(false)}
                            onBlur={() => {
                              if (!field.value) setShowTimer(true);
                            }}
                          />
                        </div>
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('description')}</FormLabel>
                      <FormControl>
                        <Textarea placeholder={t('enterDescription')} {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="typeId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('type')}</FormLabel>
                      <FormControl>
                        <UniversalCombobox
                          url="/activity-types"
                          labelKey="name"
                          valueKey="id"
                          selectedId={field.value as number | undefined}
                          onSelectItem={(item) => field.onChange(item?.id ?? undefined)}
                          placeholder={t('selectType')}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </>
            )}

            <AlertDialogFooter className="flex justify-end gap-2 mt-4">
              <AlertDialogCancel onClick={() => setOpen(false)}>
                {t('cancel')}
              </AlertDialogCancel>
              <Button type="submit" disabled={isPending}>
                {isPending ? t('saving') : t('save')}
              </Button>
            </AlertDialogFooter>
          </form>
        </Form>
      </AlertDialogContent>
    </AlertDialog>
  );
}
